"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { createPortal } from "react-dom";
import {
  IconButton,
  NotificationPanel,
  type NotificationPanelItem,
} from "@/design-system";
import { Icon } from "@/design-system/icons";
import { useOrganization } from "@/contexts/OrganizationContext";
import {
  isNotificationUnread,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from "@/hooks/useNotifications";
import { getErrorMessage } from "@/lib/query";
import { trackActivity } from "@/lib/activityTracking";
import type { AppNotification } from "@/types/notification";
import "./NotificationCenter.css";

function formatNotificationDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const elapsedMs = Date.now() - date.getTime();
  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
}

function toPanelItem(notification: AppNotification): NotificationPanelItem {
  return {
    id: notification.id,
    type: "info",
    icon: "FileText",
    title: notification.content.title,
    message: notification.content.message,
    date: formatNotificationDate(notification.createdAt),
    unread: isNotificationUnread(notification),
  };
}

export function NotificationCenter() {
  const router = useRouter();
  const { currentOrganization } = useOrganization();
  const activeOrganizationId =
    currentOrganization?._id || currentOrganization?.id || null;
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const notificationsQuery = useNotifications(activeOrganizationId);
  const markReadMutation = useMarkNotificationRead(activeOrganizationId);
  const markAllReadMutation =
    useMarkAllNotificationsRead(activeOrganizationId);

  const notifications = useMemo(
    () => notificationsQuery.data?.docs ?? [],
    [notificationsQuery.data?.docs],
  );
  const unreadCount = notificationsQuery.data?.unreadCount ?? 0;
  const panelItems = useMemo(
    () => notifications.map(toPanelItem),
    [notifications],
  );

  const closePanel = useCallback(() => {
    setOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  const openPanel = useCallback(() => {
    setHasOpened(true);
    setOpen(true);
    trackActivity(
      "app.action",
      {
        action: "notifications.panel_opened",
        unreadCount,
      },
      "Opened notifications",
    );
    void notificationsQuery.refetch();
  }, [notificationsQuery, unreadCount]);

  const handleNotificationClick = useCallback(
    (notificationId: string) => {
      const notification = notifications.find(
        (candidate) => candidate.id === notificationId,
      );
      if (!notification) return;

      trackActivity(
        "app.action",
        {
          action: "notifications.item_opened",
          notificationType: notification.content.eventName,
          wasUnread: isNotificationUnread(notification),
        },
        "Opened survey response notification",
      );

      if (isNotificationUnread(notification)) {
        markReadMutation.mutate(notification.id);
      }

      setOpen(false);
      router.push(notification.content.actionUrl);
    },
    [markReadMutation, notifications, router],
  );

  const handleMarkAllRead = useCallback(() => {
    if (unreadCount === 0 || markAllReadMutation.isPending) return;
    trackActivity(
      "app.action",
      {
        action: "notifications.mark_all_read",
        unreadCount,
      },
      "Marked all notifications read",
    );
    markAllReadMutation.mutate();
  }, [markAllReadMutation, unreadCount]);

  const error =
    notificationsQuery.isError && !notificationsQuery.data
      ? getErrorMessage(
          notificationsQuery.error,
          "Check your connection and try again.",
        )
      : null;

  return (
    <>
      <IconButton
        ref={triggerRef}
        variant="transparent"
        className="navigation__icon-btn navigation__notification-btn"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={openPanel}
        leadingVisual={
          <span className="notification-center__bell">
            <Icon name="Bell" size={18} />
            {unreadCount > 0 ? (
              <span
                className="navigation__notification-dot"
                aria-hidden="true"
              />
            ) : null}
          </span>
        }
      />

      {hasOpened && typeof document !== "undefined"
        ? createPortal(
            <NotificationPanel
              open={open}
              onClose={closePanel}
              notifications={panelItems}
              unreadCount={unreadCount}
              onNotificationClick={handleNotificationClick}
              onMarkAllRead={handleMarkAllRead}
              markAllReadBusy={markAllReadMutation.isPending}
              loading={notificationsQuery.isPending}
              error={error}
              onRetry={() => void notificationsQuery.refetch()}
              settingsHref="/settings/notifications"
            />,
            document.body,
          )
        : null}
    </>
  );
}
